import React from "react";
import "./ViewLoader.css";

/**
 * A reusable component for displaying loading state with animation
 *
 * @param {Object} props - Component props
 * @param {boolean} props.isLoading - Whether the content is currently loading
 * @param {string} props.message - The message to display during loading
 * @param {string} props.className - Additional classes to add to the wrapper
 * @param {number} [props.currentStep] - Current step for progress indication
 * @param {number} [props.totalSteps] - Total steps for progress indication
 * @param {React.ReactNode} props.children - Content to display when not loading
 * @returns {JSX.Element} The rendered component
 */
const ViewLoader = ({
  isLoading,
  message = "",
  className = "",
  currentStep,
  totalSteps,
  children,
}) => {
  const hasProgress =
    typeof totalSteps === "number" &&
    totalSteps > 0 &&
    typeof currentStep === "number";
  const safeCurrentStep = hasProgress
    ? Math.min(Math.max(currentStep, 0), totalSteps)
    : 0;
  const progressPercentage = hasProgress
    ? Math.round((safeCurrentStep / totalSteps) * 100)
    : 0;

  return (
    <div
      className={`ViewLoader-wrapper ${
        isLoading ? "loading" : ""
      } ${className}`}
    >
      {isLoading && (
        <div className="ViewLoader-overlay">
          <div className="ViewLoader-content">
            <div className="ViewLoader-spinner">
              <div className="ViewLoader-spinner-ring"></div>
              <div className="ViewLoader-spinner-ring"></div>
              <div className="ViewLoader-spinner-ring"></div>
            </div>
            <div className="ViewLoader-message">{message}</div>
            {hasProgress && (
              <div className="ViewLoader-progress">
                <div className="ViewLoader-progress-bar">
                  <div
                    className="ViewLoader-progress-fill"
                    style={{ width: `${progressPercentage}%` }}
                  >
                    <div className="ViewLoader-progress-shine"></div>
                  </div>
                </div>
                <div className="ViewLoader-progress-text">
                  {safeCurrentStep} / {totalSteps} ({progressPercentage}%)
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {children}
    </div>
  );
};

export default ViewLoader;
